/**
 * The license LEDGER — history + renew/amend (ADR-0076 decision 8).
 *
 * A renewal used to be an edit on the license row, which lost the history the vendor
 * needs at renewal time ("what did they buy last year, and for how much?"). Renewal and
 * amendment are the SAME primitive here, discriminated by `reason`, because that is what
 * they are server-side: a new emission, either after the current window (renewal) or over
 * it (amendment). The activation key is NEVER re-issued — the install picks the re-signed
 * token up at its next heartbeat, so nothing is re-sent to the organization.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  type LicenseIssuanceIn,
  type PlatformLicense,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

const REASON_LABEL: Record<string, string> = {
  initial: "Emissione",
  renewal: "Rinnovo",
  amendment: "Modifica",
};

const PERIOD_LABEL: Record<string, string> = {
  monthly: "Mensile",
  annual: "Annuale",
  custom: "Personalizzato",
};

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

/** Cents → "€ 1.200,00". Money is stored in CENTS, never a float. */
function formatAmount(cents: number | null, currency: string | null): string {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: currency || "EUR",
  }).format(cents / 100);
}

/**
 * The ledger BODY — chrome-free so it can live in the licence detail page as a section
 * AND inside the dialog below. Two copies of a renew/amend/correct form is exactly the
 * kind of duplication that starts agreeing with itself and stops agreeing with the API.
 */
export function LicenseIssuancesPanel({ license }: { license: PlatformLicense }) {
  const qc = useQueryClient();
  // "correction" is a THIRD mode, not a third kind of emission: it repairs the row in force
  // instead of adding one. The distinction is the whole point — a typo is a defect, not a
  // thing the organization bought.
  const [mode, setMode] = React.useState<"renewal" | "amendment" | "correction" | null>(null);
  const [form, setForm] = React.useState({
    billing_period: "annual",
    ends_at: "",
    amount: "",
    currency: "EUR",
    payment_ref: "",
    notes: "",
  });
  const [error, setError] = React.useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["platform", "licenses", license.id, "issuances"],
    queryFn: () => platformApi.listLicenseIssuances(license.id),
  });

  /** Open the correction form seeded with what the current emission actually says — a
   * repair starts from the value being repaired, not from an empty form. */
  function startCorrection() {
    const cur = license.current_issuance;
    setForm((f) => ({
      ...f,
      ends_at: cur?.ends_at ? cur.ends_at.slice(0, 10) : "",
      amount: cur?.amount_cents != null ? String(cur.amount_cents / 100) : "",
      currency: cur?.currency || "EUR",
      payment_ref: cur?.payment_ref || "",
    }));
    setError(null);
    setMode("correction");
  }

  const correct = useMutation({
    mutationFn: async () => {
      const cur = license.current_issuance;
      if (!cur) throw new Error("Nessuna emissione in vigore");
      const amount = form.amount.trim();
      return platformApi.correctLicenseIssuance(license.id, cur.id, {
        ...(form.ends_at ? { ends_at: new Date(`${form.ends_at}T23:59:59Z`).toISOString() } : {}),
        amount_cents: amount ? Math.round(Number(amount) * 100) : null,
        currency: amount ? form.currency : null,
        payment_ref: form.payment_ref.trim() || null,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["platform", "licenses"] });
      setMode(null);
      setError(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof PlatformApiError ? e.message : "Correzione non riuscita"),
  });

  const save = useMutation({
    mutationFn: async () => {
      const amount = form.amount.trim();
      const body: LicenseIssuanceIn = {
        reason: mode as "renewal" | "amendment",
        billing_period: form.billing_period as LicenseIssuanceIn["billing_period"],
        // An amendment reuses the current window server-side, so an end date is only
        // meaningful on a renewal — and only when the period is `custom`.
        ends_at: mode === "renewal" && form.ends_at ? new Date(form.ends_at).toISOString() : null,
        amount_cents: amount ? Math.round(Number(amount) * 100) : null,
        currency: amount ? form.currency : null,
        payment_ref: form.payment_ref.trim() || null,
        notes: form.notes.trim() || null,
      };
      return platformApi.createLicenseIssuance(license.id, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["platform", "licenses"] });
      setMode(null);
      setError(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof PlatformApiError ? e.message : "Emissione non riuscita"),
  });

  const rows = listQ.data ?? [];
  const needsEndDate = mode === "renewal" && form.billing_period === "custom";

  return (
    <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Ogni rinnovo o modifica è una nuova emissione: la chiave di attivazione non cambia mai e
            l&apos;installazione riceve il token rifirmato al prossimo heartbeat. Non c&apos;è nulla
            da rimandare all&apos;organizzazione.
          </p>

          {rows.length === 0 && !listQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Nessuna emissione registrata.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border/60">
              <Table>
                <Thead>
                  <Tr>
                    <Th>Tipo</Th>
                    <Th>Periodo</Th>
                    <Th>Validità</Th>
                    <Th className="text-right">Importo</Th>
                    <Th>Pagato</Th>
                    <Th>Riferimento</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((iss) => (
                    <Tr key={iss.id}>
                      <Td>
                        <div className="flex items-center gap-2">
                          <span>{REASON_LABEL[iss.reason] ?? iss.reason}</span>
                          {iss.id === license.current_issuance?.id && (
                            <>
                              <Badge tone="active">In vigore</Badge>
                              <button
                                type="button"
                                className="text-xs text-muted-foreground underline hover:text-foreground"
                                onClick={startCorrection}
                              >
                                Correggi
                              </button>
                            </>
                          )}
                        </div>
                      </Td>
                      <Td className="text-muted-foreground">
                        {PERIOD_LABEL[iss.billing_period] ?? iss.billing_period}
                      </Td>
                      <Td className="whitespace-nowrap">
                        {fmtDate(iss.starts_at)} → {fmtDate(iss.ends_at)}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {formatAmount(iss.amount_cents, iss.currency)}
                      </Td>
                      <Td className="text-muted-foreground">{fmtDate(iss.paid_at)}</Td>
                      <Td className="text-muted-foreground">{iss.payment_ref || "—"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          )}

          {mode === null ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setMode("renewal")}>
                Rinnova
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode("amendment")}>
                Modifica il periodo in corso
              </Button>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-border/60 p-3">
              <p className="text-sm font-medium">
                {mode === "renewal"
                  ? "Nuovo rinnovo"
                  : mode === "amendment"
                    ? "Modifica del periodo in corso"
                    : "Correzione dell'emissione in vigore"}
              </p>
              <p className="text-xs text-muted-foreground">
                {mode === "renewal"
                  ? "Parte dalla fine dell'emissione in vigore e ne eredita gli entitlements."
                  : mode === "amendment"
                    ? "Stessa finestra dell'emissione in vigore, che viene sostituita: la data di rinnovo dell'organizzazione non si sposta."
                    : "Ripara un errore di compilazione sull'emissione in vigore: non nasce una nuova emissione e lo storico non cresce. Per cambiare ciò che è stato venduto usa invece «Modifica il periodo in corso»."}
              </p>
              {mode === "correction" && (
                <div className="space-y-1.5 sm:max-w-xs">
                  <Label htmlFor="li-fix-ends">Scadenza</Label>
                  <Input
                    id="li-fix-ends"
                    type="date"
                    value={form.ends_at}
                    onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                  />
                </div>
              )}
              {mode === "renewal" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="li-period">Periodo</Label>
                    <select
                      id="li-period"
                      className="w-full"
                      value={form.billing_period}
                      onChange={(e) => setForm((f) => ({ ...f, billing_period: e.target.value }))}
                    >
                      <option value="annual">Annuale</option>
                      <option value="monthly">Mensile</option>
                      <option value="custom">Personalizzato</option>
                    </select>
                  </div>
                  {needsEndDate && (
                    <div className="space-y-1.5">
                      <Label htmlFor="li-ends">Scadenza</Label>
                      <Input
                        id="li-ends"
                        type="date"
                        value={form.ends_at}
                        onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="li-amount">Importo</Label>
                  <Input
                    id="li-amount"
                    inputMode="decimal"
                    placeholder="1200"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="li-currency">Valuta</Label>
                  <Input
                    id="li-currency"
                    maxLength={3}
                    value={form.currency}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="li-ref">Riferimento</Label>
                  <Input
                    id="li-ref"
                    placeholder="Fattura 2026/114"
                    value={form.payment_ref}
                    onChange={(e) => setForm((f) => ({ ...f, payment_ref: e.target.value }))}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Importo e riferimento sono informativi: servono allo storico e al gestionale, Tray
                non emette fatture.
              </p>
              {error && <p className="text-sm text-danger">{error}</p>}
              <div className="flex gap-2">
                {mode === "correction" ? (
                  <Button size="sm" disabled={correct.isPending} onClick={() => correct.mutate()}>
                    {correct.isPending ? "Correggo…" : "Salva correzione"}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={save.isPending || (needsEndDate && !form.ends_at)}
                    onClick={() => save.mutate()}
                  >
                    {save.isPending ? "Emetto…" : "Emetti"}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
                  Annulla
                </Button>
              </div>
            </div>
          )}
    </div>
  );
}

/** The same ledger in a dialog — kept for surfaces that open it in place. */
export function LicenseIssuancesDialog({
  license,
  onClose,
}: {
  license: PlatformLicense;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Emissioni — {license.organization}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <LicenseIssuancesPanel license={license} />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
